import { useState, useEffect } from 'react'
import { Calendar as CalIcon, X, ChevronLeft, ChevronRight, Bell, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDraggable, loadReminders, saveReminders, type Reminder } from '@/hooks/useWidgetUtils'

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

function dateKey(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

export function CalendarWidget({ open, onClose }: { open: boolean; onClose: () => void }) {
  const today = new Date()
  const [viewYear, setViewYear] = useState(today.getFullYear())
  const [viewMonth, setViewMonth] = useState(today.getMonth())
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [noteText, setNoteText] = useState('')
  const [noteTime, setNoteTime] = useState('09:00')
  const [reminders, setReminders] = useState<Reminder[]>([])
  const { offset, handleProps, resetOffset } = useDraggable()

  useEffect(() => { if (open) { resetOffset(); setReminders(loadReminders()) } }, [open, resetOffset])

  if (!open) return null

  const firstOfMonth = new Date(viewYear, viewMonth, 1)
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const startWeekday = firstOfMonth.getDay()

  const cells: (number | null)[] = []
  for (let i = 0; i < startWeekday; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  function goPrev() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1) }
    else setViewMonth(viewMonth - 1)
  }
  function goNext() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1) }
    else setViewMonth(viewMonth + 1)
  }

  function selectDay(day: number) {
    const key = dateKey(viewYear, viewMonth, day)
    setSelectedDate(key === selectedDate ? null : key)
    setNoteText('')
    setNoteTime('09:00')
  }

  // Real browser Notification permission requested at the moment of actually
  // setting a reminder, not on load -- asking before the person has done
  // anything that needs it tends to just get silently denied forever.
  async function setReminder() {
    if (!selectedDate || !noteText.trim()) return
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission()
    }
    const newReminder: Reminder = {
      id: `${selectedDate}-${Date.now()}`,
      date: selectedDate,
      time: noteTime,
      note: noteText.trim(),
      fired: false,
    }
    const updated = [...reminders, newReminder]
    setReminders(updated)
    saveReminders(updated)
    setNoteText('')
  }

  function deleteReminder(id: string) {
    const updated = reminders.filter(r => r.id !== id)
    setReminders(updated)
    saveReminders(updated)
  }

  const isCurrentMonth = viewYear === today.getFullYear() && viewMonth === today.getMonth()
  const remindersOnSelected = selectedDate ? reminders.filter(r => r.date === selectedDate) : []
  const datesWithReminders = new Set(reminders.map(r => r.date))

  return (
    <div
      className="fixed z-[70] rounded-2xl shadow-2xl backdrop-blur-xl border overflow-hidden
        bottom-4 right-4 w-[300px]
        max-[480px]:left-4 max-[480px]:right-4 max-[480px]:bottom-4 max-[480px]:w-auto"
      style={{
        background: 'var(--color-surface)',
        borderColor: 'rgba(255,255,255,0.12)',
        transform: `translate(${offset.x}px, ${offset.y}px)`,
      }}
    >
      <div
        className="flex items-center justify-between px-4 pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none"
        {...handleProps}
      >
        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-text-muted">
          <CalIcon className="h-3.5 w-3.5" />
          Calendar
        </div>
        <button onClick={onClose} className="text-text-muted hover:text-text-primary transition-colors cursor-pointer p-0.5" aria-label="Close calendar">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="px-4 pb-2 flex items-center justify-between">
        <button onClick={goPrev} className="text-text-muted hover:text-text-primary transition-colors cursor-pointer p-1" aria-label="Previous month">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-medium text-text-primary">{MONTH_NAMES[viewMonth]} {viewYear}</span>
        <button onClick={goNext} className="text-text-muted hover:text-text-primary transition-colors cursor-pointer p-1" aria-label="Next month">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="px-4 pb-3">
        <div className="grid grid-cols-7 gap-1 mb-1">
          {DAY_LABELS.map((d, i) => (
            <div key={i} className="text-center text-[10px] text-text-muted font-medium py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, i) => {
            const isToday = isCurrentMonth && day === today.getDate()
            const key = day !== null ? dateKey(viewYear, viewMonth, day) : null
            const isSelected = key !== null && key === selectedDate
            const hasReminder = key !== null && datesWithReminders.has(key)
            return (
              <button
                key={i}
                type="button"
                disabled={day === null}
                onClick={() => day !== null && selectDay(day)}
                className={cn(
                  'relative aspect-square flex items-center justify-center text-xs rounded-lg transition-colors',
                  day === null ? 'cursor-default' : 'cursor-pointer',
                  isSelected ? 'bg-indigo-600 text-text-primary font-semibold'
                    : isToday ? 'bg-surface-elevated text-text-primary font-semibold'
                    : day !== null ? 'text-text-secondary hover:bg-surface-elevated' : ''
                )}
              >
                {day ?? ''}
                {hasReminder && !isSelected && (
                  <span className="absolute bottom-0.5 w-1 h-1 rounded-full bg-amber-400" />
                )}
              </button>
            )
          })}
        </div>
      </div>

      {selectedDate && (
        <div className="px-4 pb-4 border-t border-border pt-3">
          <div className="text-[11px] text-text-muted mb-2">{selectedDate}</div>

          {remindersOnSelected.length > 0 && (
            <div className="mb-2 space-y-1">
              {remindersOnSelected.map(r => (
                <div key={r.id} className="flex items-center gap-2 bg-surface-elevated rounded-lg px-2.5 py-1.5">
                  <Bell className={cn('h-3 w-3 shrink-0', r.fired ? 'text-text-muted' : 'text-amber-400')} />
                  <span className="text-[11px] text-text-muted shrink-0">{r.time}</span>
                  <span className="text-xs text-text-secondary truncate flex-1">{r.note}</span>
                  <button onClick={() => deleteReminder(r.id)} className="text-text-muted hover:text-red-400 transition-colors cursor-pointer shrink-0" aria-label="Delete reminder">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-1.5">
            <input
              type="time"
              value={noteTime}
              onChange={e => setNoteTime(e.target.value)}
              className="bg-surface-elevated border border-border rounded-lg px-2 py-1.5 text-xs text-text-primary w-[88px] shrink-0 focus:outline-none focus:border-indigo-500/40"
            />
            <input
              type="text"
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && setReminder()}
              placeholder="Reminder note..."
              className="flex-1 min-w-0 bg-surface-elevated border border-border rounded-lg px-2.5 py-1.5 text-xs text-text-primary placeholder-text-muted focus:outline-none focus:border-indigo-500/40"
            />
            <button
              onClick={setReminder}
              disabled={!noteText.trim()}
              className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-30 disabled:cursor-not-allowed text-text-primary text-xs font-medium transition-colors cursor-pointer shrink-0"
            >
              Set
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
